import { EyeOutlined, LockOutlined } from '@ant-design/icons';
import {
    type OrgEmailActivityAction,
    OrgEmailActivityDrawer,
} from '@client/components/_business/email/OrgEmailActivityDrawer';
import { PageController } from '@client/components/_core/layout/PageController';
import { LastEmailStatus } from '@client/components/_ui/data-display/LastEmailStatus';
import { ProviderTag } from '@client/components/_ui/data-display/ProviderTag';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ProtectedTaggedList } from '@client/components/_ui/display/ProtectedTaggedList';
import { TruncatedList } from '@client/components/_ui/display/TruncatedList';
import { UserFilters } from '@client/components/users/UserFilters';
import { tokenValues } from '@client/design-system/__generated__';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
// Import the new table system
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { getRoleColor, getRoleDisplayName, getRoleIcon } from '@client/utils/roles/role-config';
import { DEFAULT_PAGE_SIZE, ORG_ROLES, USER_SYSTEM_USAGES, type UserSystemUsage } from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute } from '@tanstack/react-router';
import { Avatar, Badge, Tooltip, Typography } from 'antd';
import clsx from 'clsx';
import { Clock, UserPlus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { match } from 'ts-pattern';

// Import components from the users page
import {
    type QuickFilterType,
    UserQuickFilters,
} from '../~configuration/~org-settings/~users/components/UserQuickFilters';
import { InviteUser } from '../~configuration/~org-settings/~users/InviteUser';

export const Route = createFileRoute('/console/$orgId/debug/')({
    staticData: {
        metadata: { title: 'Debug - New table demo' },
        ui: {
            title: 'New table system demo',
            subtitle: 'Demonstration of the new PrismaTableV2 component with users data',
        },
    },
    component: DebugTableDemo,
});

// Type definitions
type QueryVariables = RouterInputs['user']['listUsers'];
type UserListOutput = RouterOutputs['user']['listUsers'];
type UserRow = NonNullable<UserListOutput>['rows'][number];
type UserOrderBy = NonNullable<QueryVariables['orderBy']>;
type Where = QueryVariables['where'];

const DEFAULT_FILTER = {
    AND: [{ userId: { gt: 1n } }, { isSupportAccount: false }],
} satisfies QueryVariables['where'];

// Helper function for relative time formatting
const formatRelativeTime = (date: Date | null) => {
    if (!date) return 'Never';
    const dateTime = DateTime.fromJSDate(date);
    return dateTime.toRelative() || 'Never';
};

// Stable accessor function to prevent re-renders (fixes antd ResizeObserver infinite loop #53869)
const roleAccessor = (role: string) => ({
    label: getRoleDisplayName(role),
    color: getRoleColor(role),
    icon: getRoleIcon(role, 12),
});

function DebugTableDemo() {
    const [openInviteUserDialog, setOpenInviteUserDialog] = useState(false);
    const [quickFilter, setQuickFilter] = useState<QuickFilterType>('all');
    const [orgEmailActivityAction, setOrgEmailActivityAction] = useState<OrgEmailActivityAction>(['none']);
    const [filterWhere, setFilterWhere] = useState<Where>({});

    const user = useUser();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const goToUser = (userId: bigint) =>
        navigate({
            to: '/console/$orgId/configuration/org-settings/users/details/$userId',
            params: { orgId, userId },
        });
    const { can } = usePermissions();
    const canAdminOrg = can('org:admin');
    const { formatDate } = useFormatter();
    const canInviteUsers = hasPermission(user.roles, 'user:create');

    // Create column helper with proper types
    const columnHelper = createColumnHelper<UserRow, UserOrderBy>();

    // Query parameters state
    const [queryVariables, setQueryVariables] = usePathState<RouterInputs['user']['listUsers']>('queryVariables', {
        orderBy: [{ firstName: 'asc' }],
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });

    // Apply quick filters
    const quickFilterWhere = useMemo<Where>(() => {
        return match(quickFilter)
            .with('active', () => ({ disabled: false }) satisfies Where)
            .with('inactive', () => ({ disabled: true }) satisfies Where)
            .with('admins', () => ({ roles: { has: ORG_ROLES.ORG_ADMIN } }) satisfies Where)
            .with('employees', () => ({ isEmployee: true }) satisfies Where)
            .with('all', () => ({}) satisfies Where)
            .exhaustive();
    }, [quickFilter]);

    // Merge all filters
    const mergedWhere: Where = {
        AND: [
            ...DEFAULT_FILTER.AND,
            ...(filterWhere ? [filterWhere] : []),
            ...(quickFilterWhere && Object.keys(quickFilterWhere).length > 0 ? [quickFilterWhere] : []),
        ] as NonNullable<Where>['AND'],
    };

    // Fetch data
    const { data, isFetching, refetch } = trpc.user.listUsers.useQuery({
        ...queryVariables,
        where: mergedWhere,
    });

    // Define columns using the new column helper - memoized to prevent re-renders
    const columns = useMemo(
        () => [
            // User column with avatar
            columnHelper.accessor('firstName', {
                id: 'name',
                header: 'User',
                sortable: 'firstName',
                cell: ({ row }) => (
                    <div className="flex items-center gap-3">
                        <Avatar src={row.photoUrl} size={36} className="flex-shrink-0">
                            {!row.photoUrl && `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`}
                        </Avatar>
                        <div className="min-w-0">
                            <div className="truncate font-medium text-foreground">
                                {row.firstName} {row.lastName}
                            </div>
                            <div className="truncate text-subtle-foreground text-xs">ID: {Number(row.userId)}</div>
                        </div>
                    </div>
                ),
                fixed: 'left',
                width: 280,
                hideable: false,
            }),

            // Position column
            columnHelper.accessor('position', {
                id: 'position',
                header: 'Position',
                sortable: 'position',
                cell: ({ row }) => row.position || <span className="text-subtle-foreground">-</span>,
                width: 150,
            }),

            // Email column - email is at the top level of UserRow
            columnHelper.accessor('email', {
                id: 'email',
                header: 'Email',
                sortable: 'User.email',
                cell: ({ row }) => (
                    <Typography.Link href={`mailto:${row.email}`} copyable={{ text: row.email }} className="text-sm">
                        {row.email}
                    </Typography.Link>
                ),
            }),

            // Status column
            columnHelper.accessor('disabled', {
                id: 'status',
                header: 'Status',
                sortable: 'disabled',
                cell: ({ row }) => (
                    <Badge
                        status={row.disabled ? 'error' : 'success'}
                        text={
                            <span className={clsx('font-medium', row.disabled ? 'text-red-600' : 'text-green-600')}>
                                {row.disabled ? 'Disabled' : 'Active'}
                            </span>
                        }
                    />
                ),
                width: 120,
                align: 'center',
            }),

            // Permissions column
            columnHelper.display({
                id: 'permissions',
                header: 'Permissions',
                cell: ({ row }) => (
                    <div className="flex justify-center gap-2">
                        {row.canViewUnallocatedClaims && (
                            <Tooltip title="Can view unallocated claims">
                                <Badge
                                    count={<EyeOutlined style={{ color: tokenValues.color.role.green }} />}
                                    className="cursor-help"
                                />
                            </Tooltip>
                        )}
                    </div>
                ),
                width: 150,
                align: 'center',
            }),

            // System Usage column
            columnHelper.accessor('systemUsage', {
                id: 'systemUsage',
                header: 'System Usage',
                sortable: 'systemUsage',
                cell: ({ row }) => (
                    <span className="text-muted-foreground text-sm">
                        {USER_SYSTEM_USAGES[row.systemUsage as UserSystemUsage] || row.systemUsage || '-'}
                    </span>
                ),
                width: 180,
            }),

            // Roles column
            columnHelper.accessor('roles', {
                id: 'roles',
                header: 'Roles',
                sortable: false,
                cell: ({ row }) => {
                    const hasOrgAdminRole = row.roles?.includes(ORG_ROLES.ORG_ADMIN);
                    const canEdit = hasPermission(user.roles, 'super:admin') || canAdminOrg || !hasOrgAdminRole;

                    return (
                        <ProtectedTaggedList
                            items={row.roles || []}
                            accessor={roleAccessor}
                            emptyText="No roles"
                            isProtected={hasOrgAdminRole && !canEdit}
                            protectionTooltip="Admin role protected"
                            protectionIcon={<LockOutlined className="text-orange-500" />}
                            additionalItemsTitle="Additional Roles:"
                            displayLimit={2}
                        />
                    );
                },
                width: 200,
            }),

            // Handling Parties column
            columnHelper.accessor('HandlingParties', {
                id: 'handlingParties',
                header: 'Handling Parties',
                sortable: false,
                cell: ({ row }) => (
                    <TruncatedList items={row.HandlingParties || []} getItemLabel={(party: any) => party.name} />
                ),
                width: 200,
            }),

            // Provider column - provider is at top level after spreading User
            columnHelper.accessor('provider', {
                id: 'provider',
                header: 'Authentication Provider',
                sortable: 'User.provider',
                cell: ({ row }) => <ProviderTag provider={row.provider} />,
                width: 150,
            }),

            // Last Email Status column
            columnHelper.accessor('lastEmailStatus', {
                id: 'lastEmailStatus',
                header: 'Last Email Status',
                // Sort by the per-org OrgUserEmailStatus view (what this table displays via
                // user.listUsers), not the global User.lastEmailStatus column.
                sortable: 'LastEmailStatus.lastEmailStatus',
                cell: ({ row }) => (
                    <LastEmailStatus
                        status={row.lastEmailStatus}
                        onClick={() =>
                            setOrgEmailActivityAction([
                                'view',
                                {
                                    userId: row.userId,
                                    userEmail: row.email,
                                },
                            ])
                        }
                    />
                ),
                width: 150,
                align: 'center',
            }),

            // Last login column
            columnHelper.accessor('lastLoginAt', {
                id: 'lastLoginAt',
                header: 'Last login',
                sortable: 'lastLoginAt',
                cell: ({ row }) => (
                    <Tooltip title={row.lastLoginAt ? formatDate(row.lastLoginAt, 'date-time') : 'Never logged in'}>
                        <div className="flex items-center gap-1 text-muted-foreground text-sm">
                            <Clock size={14} className="text-subtle-foreground" />
                            <span className="capitalize">{formatRelativeTime(row.lastLoginAt)}</span>
                        </div>
                    </Tooltip>
                ),
                width: 120,
            }),

            // Created column
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Created',
                sortable: 'createdAt',
                cell: ({ row }) => (
                    <span className="text-muted-foreground text-sm">{formatDate(row.createdAt, 'date-time')}</span>
                ),
                width: 150,
            }),
        ],
        [columnHelper, formatDate, setOrgEmailActivityAction, canAdminOrg, user.roles],
    );

    // Use the new table hook with flat API
    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'userId',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both', // Show pagination at both top and bottom
            //showQuickJumper: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'debug-users-table-columns-v2',
            enableReordering: true, // Enable drag-and-drop column reordering
            defaultHidden: ['permissions', 'systemUsage', 'handlingParties', 'provider', 'createdAt'],
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        sticky: true,
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true, // Persist selection across page changes
            getCheckboxProps: (row) => ({
                disabled: row.disabled,
            }),
        },
    });

    return (
        <PageController route={Route}>
            {/* Quick Filters */}
            <UserQuickFilters quickFilter={quickFilter} setQuickFilter={setQuickFilter} totalCount={data?.total} />

            {/* Filter Bar */}
            <div className="mb-4 flex items-start gap-4">
                <div className="flex-1">
                    <UserFilters
                        urlKey="debug-user-filters"
                        onChange={(where) => {
                            setFilterWhere(where);
                            setQueryVariables((prev) => ({ ...prev, offset: 0 }));
                        }}
                    />
                </div>
                {canInviteUsers && (
                    <Button type="primary" icon={<UserPlus />} onClick={() => setOpenInviteUserDialog(true)}>
                        Invite user
                    </Button>
                )}
            </div>

            {/* Demo Info Banner */}
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h3 className="mb-2 font-semibold text-blue-900">🎉 New PrismaTableV2 Features</h3>
                <ul className="space-y-1 text-blue-800 text-sm">
                    <li>✅ Built-in pagination at top and bottom</li>
                    <li>✅ Column visibility selector (click the columns icon)</li>
                    <li>✅ Drag-and-drop column reordering</li>
                    <li>✅ Refresh button integrated</li>
                    <li>✅ Type-safe column definitions with column helper</li>
                    <li>✅ Persistent column preferences</li>
                    <li>✅ Row selection persists across pages</li>
                    <li>✅ Custom left content in top pagination</li>
                    <li>✅ Clear selection button with count display</li>
                </ul>
            </div>

            {/* The New Table Component */}
            <PrismaTable
                {...tableProps}
                onRow={(row) => {
                    const targetUserIsOrgAdmin = row.roles?.includes(ORG_ROLES.ORG_ADMIN);
                    const canEditThisUser =
                        hasPermission(user.roles, 'super:admin') || canAdminOrg || !targetUserIsOrgAdmin;

                    return {
                        className: clsx(
                            'transition-colors',
                            row.disabled && 'opacity-60',
                            canEditThisUser ? 'cursor-pointer hover:bg-muted' : 'cursor-not-allowed',
                        ),
                        onClick: (e) => {
                            // Don't trigger row click if clicking on action buttons
                            const target = e.target as HTMLElement;
                            if (target.closest('.ant-dropdown-trigger') || target.closest('.ant-typography-copy')) {
                                return;
                            }
                            if (canEditThisUser) {
                                void goToUser(row.userId);
                            }
                        },
                    };
                }}
            />

            {/* Modals and Drawers */}
            {canInviteUsers && (
                <InviteUser
                    open={openInviteUserDialog}
                    onClose={() => {
                        setOpenInviteUserDialog(false);
                        void refetch();
                    }}
                    onInviteSuccess={(userId) => {
                        setOpenInviteUserDialog(false);
                        void refetch();
                        void goToUser(userId);
                    }}
                />
            )}

            <OrgEmailActivityDrawer
                action={orgEmailActivityAction}
                onClose={() => {
                    setOrgEmailActivityAction(['none']);
                }}
            />
        </PageController>
    );
}
